/* ============================================================
   BAG — the shared bag, wired to the Shopify cart (docs/PORT.md §7).
   The panel UI (slide-in, steppers, counts, bump) is unchanged from
   the source build. The store layer now speaks Shopify:
     add            → POST /cart/add.js with variant ids
     change/remove  → POST /cart/change.js by line key
     read/counts    → GET  /cart.js
   The Shopify cart is the source of truth; cross-tab and back-forward
   sync is a re-fetch of /cart.js. Checkout hands off to /checkout.
   ============================================================ */

import { PRODUCTS, PAGE } from './pdp-data.js';
import { money } from './money.js';

const CFG = JSON.parse(document.getElementById('sbmd-config').textContent);
const VARIANT_OF = {};
const ID_OF = {};
for (const [id, p] of Object.entries(CFG.products)) { VARIANT_OF[id] = p.variantId; ID_OF[p.variantId] = id; }

const $  = (s, c = document) => c.querySelector(s);
const $$ = (s, c = document) => [...c.querySelectorAll(s)];
const esc = v => String(v ?? '').replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

/* ---------- store — the Shopify cart ---------- */
let lines = [];                 /* [{ key, id|null, qty, lineCents, wasCents, title, url, image }] */
let itemCount = 0;
let totalCents = 0;             /* cart.total_price: after any discount code */
let discountCode = '';          /* the applied code's own label, e.g. "TRY" (never hard-coded) */
let shipsFree = false;          /* TRY is on: layout/theme.liquid pairs it with TRYFREESHIP */

/* Free-shipping nudge: over $90 and under $150 the subtotal line counts down
   to the $150 line, standing in for the note under Checkout. Same rule as
   sections/cart.liquid. */
const FREE_SHIP_CENTS = 15000;
const NUDGE_FROM_CENTS = 9000;

function shipNudge() {
  const short = FREE_SHIP_CENTS - totalCents;
  if (shipsFree || totalCents <= NUDGE_FROM_CENTS || short <= 0) return '';
  return `Add ${money(short)} more to get free shipping.`;
}

function absorb(cart) {
  lines = cart.items.map(it => ({
    key: it.key,
    id: ID_OF[it.variant_id] || null,
    qty: it.quantity,
    lineCents: it.final_line_price ?? it.line_price,          /* already discounted */
    wasCents: it.original_line_price ?? it.line_price,
    title: it.product_title,
    url: it.url,
    image: it.image,
  }));
  itemCount = cart.item_count;
  totalCents = cart.total_price ?? cart.items_subtotal_price;
  discountCode = (cart.discount_codes || []).find(d => d.applicable !== false)?.code || (cart.discount_codes || [])[0]?.code || '';
  shipsFree = (cart.discount_codes || []).some(d => ['TRY', 'TRYFREESHIP'].includes(String(d.code).toUpperCase()));
}

async function refresh(bump) {
  try {
    const res = await fetch(`${CFG.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    absorb(await res.json());
  } catch (e) { console.warn('bag: cart fetch failed', e); }
  render(); counts(bump);
}

async function add(ids, opts = {}) {
  const items = [].concat(ids).map(id => VARIANT_OF[id]).filter(Boolean).map(v => ({ id: v, quantity: 1 }));
  if (items.length) {
    try {
      await fetch(`${CFG.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
    } catch (e) { console.warn('bag: add failed', e); }
  }
  await refresh(true);
  if (opts.open !== false) open();
}

async function setQty(key, qty) {
  try {
    const res = await fetch(`${CFG.routes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    });
    absorb(await res.json());
  } catch (e) { console.warn('bag: change failed', e); }
  render(); counts();
}

/* ---------- nav count ---------- */
function counts(bump) {
  $$('.bag-count').forEach(c => {
    c.textContent = `( ${itemCount} )`;
    if (bump) { c.classList.remove('is-bumped'); void c.offsetWidth; c.classList.add('is-bumped'); }
  });
}

/* ---------- panel ---------- */
document.body.insertAdjacentHTML('beforeend', `
  <div class="bag-scrim" aria-hidden="true"></div>
  <aside class="bag-panel" aria-label="Shopping bag" aria-hidden="true">
    <div class="bag-head">
      <p class="label">Your bag</p>
      <button class="bag-close" type="button" aria-label="Close bag">✕</button>
    </div>
    <div class="bag-body" data-bag-body></div>
    <div class="bag-foot" hidden data-bag-foot>
      <div class="bag-subtotal"><span>Subtotal</span><span data-bag-subtotal></span><span class="bag-nudge" data-bag-nudge hidden></span></div>
      <button class="btn btn--primary bag-checkout" type="button" data-bag-checkout>Checkout</button>
      <button class="btn--underline bag-continue" type="button" data-bag-continue>Continue shopping</button>
      <p class="fine bag-note" data-bag-note>Free shipping on orders over $150.</p>
    </div>
  </aside>`);
const panel = $('.bag-panel'), scrim = $('.bag-scrim');
const bagBody = $('[data-bag-body]'), bagFoot = $('[data-bag-foot]');

function render() {
  if (!lines.length) {
    bagBody.innerHTML = `<div class="bag-empty"><p>Your bag is empty.</p><a class="btn btn--outline btn--sm" href="${CFG.shopUrl}">Shop the formulas</a></div>`;
    bagFoot.hidden = true;
    return;
  }
  bagBody.innerHTML = lines.map(l => {
    const p = l.id ? PRODUCTS[l.id] : null;
    const href = p ? PAGE[l.id] : l.url;
    const img = p ? p.cutout : l.image;
    const name = p ? p.short : l.title;
    const discounted = l.wasCents !== l.lineCents;
    const code = esc(discountCode);
    const price = discounted
      ? `<span class="sr-only">${code ? `Price with code ${code}` : 'Discounted price'} </span>${money(l.lineCents)} <s class="bag-item-was"><span class="sr-only">Regular price </span>${money(l.wasCents)}</s>`
      : money(l.lineCents);
    const tag = discounted && code ? `<span class="bag-tag">${code}</span>` : '';
    return `
    <div class="bag-item" data-key="${l.key}">
      <a class="bag-item-thumb" href="${href}" tabindex="-1"><img src="${img}" alt=""></a>
      <div class="bag-item-info">
        <a class="bag-item-name" href="${href}">${name}</a>
        <div class="bag-qty">
          <button type="button" data-step="-1" aria-label="One fewer ${name}">&minus;</button>
          <span aria-live="polite">${l.qty}</span>
          <button type="button" data-step="1" aria-label="One more ${name}">+</button>
        </div>
      </div>
      <div class="bag-item-end">
        ${tag}
        <span class="bag-item-price">${price}</span>
        <button class="bag-remove fine" type="button" data-remove>Remove</button>
      </div>
    </div>`;
  }).join('');
  bagFoot.hidden = false;
  $('[data-bag-subtotal]').textContent = money(totalCents);
  const nudge = shipNudge();
  $('[data-bag-nudge]').textContent = nudge;
  $('[data-bag-nudge]').hidden = !nudge;
  $('[data-bag-note]').hidden = !!nudge;
}

/* ---------- open / close ---------- */
let lastFocus = null;
function open() {
  lastFocus = document.activeElement;
  render();
  document.documentElement.classList.add('bag-open');
  panel.removeAttribute('aria-hidden');
  setTimeout(() => $('.bag-close').focus(), 200);
}
function close() {
  document.documentElement.classList.remove('bag-open');
  panel.setAttribute('aria-hidden', 'true');
  lastFocus?.focus?.();
}
scrim.addEventListener('click', close);
$('.bag-close').addEventListener('click', close);
document.addEventListener('keydown', e => {
  if (e.key === 'Escape' && document.documentElement.classList.contains('bag-open')) close();
});

/* ---------- item controls ---------- */
bagBody.addEventListener('click', e => {
  const row = e.target.closest('.bag-item');
  if (!row) return;
  const key = row.dataset.key;
  const line = lines.find(l => l.key === key);
  if (!line) return;
  if (e.target.closest('[data-remove]')) return setQty(key, 0);
  const step = e.target.closest('[data-step]');
  if (step) setQty(key, Math.max(0, line.qty + Number(step.dataset.step)));
});

/* ---------- checkout — the Shopify handoff ---------- */
$('[data-bag-checkout]').addEventListener('click', () => { window.location.href = '/checkout'; });
$('[data-bag-continue]').addEventListener('click', close);

/* ---------- wiring: the nav bag opens the panel. Adds arrive through
   shopify-cart.js, which owns every [data-add] / [data-add-variant]
   button and calls SBMDbag.refresh + open when the POST lands — one
   owner per concern, no double add. ---------- */
document.addEventListener('click', e => {
  if (e.target.closest('.nav-bag')) { e.preventDefault(); open(); }
});

/* other tabs / bfcache returns stay in sync — the cart is the truth */
addEventListener('pageshow', () => refresh());
document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });

localStorage.removeItem('sbmd-bag-v1');   /* pre-wiring local bag, superseded by the real cart */
refresh();

window.SBMDbag = { add, setQty, open, close, refresh, count: () => itemCount, subtotal: () => totalCents / 100 };
